import React, { useEffect, useState } from "react";
import MatchApi from "../api/MatchApi";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { Chat } from "../components/Chat";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";

export const Connections = ({ eventID, eventData }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState({});
  const [userConnections, setUserConnections] = useState({});
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [openChats, setOpenChats] = useState(new Set());

  const toggleUser = (userId) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleChat = (matchId) => {
    setOpenChats((prev) => {
      const next = new Set(prev);
      if (next.has(matchId)) {
        next.delete(matchId);
      } else {
        next.add(matchId);
      }
      return next;
    });
  };

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const connectionsData = await MatchApi.fetchMatchesByEventID(eventID);
        setConnections(connectionsData);

        // Fetch user details for all connected users
        const userIds = new Set();
        connectionsData.forEach(connection => {
          connection.users.forEach(userId => userIds.add(userId));
        });

        setUserDetails(eventData.eventUsers);

        // Group connections by user
        const connectionsByUser = {};

        connectionsData.forEach(connection => {
          const [user1Id, user2Id] = connection.users;
          const timestamp = connection[`${user1Id}-on`]?.toDate();

          // Add connection to user1's list
          if (!connectionsByUser[user1Id]) {
            connectionsByUser[user1Id] = [];
          }
          connectionsByUser[user1Id].push({
            userId: user2Id,
            timestamp,
            matchId: connection.id,
          });

          // Add connection to user2's list
          if (!connectionsByUser[user2Id]) {
            connectionsByUser[user2Id] = [];
          }
          connectionsByUser[user2Id].push({
            userId: user1Id,
            timestamp,
            matchId: connection.id,
          });
        });
        setUserConnections(connectionsByUser);
      } catch (error) {
        console.error("Error fetching connections:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchConnections();
  }, [eventID]);


  if (loading) {
    return <div>Loading connections...</div>;
  }

  if (connections.length === 0) {
    return <div>No connections found.</div>;
  }

  return (
    <div className="space-y-4">
      {Object.entries(userConnections).map(([userId, connections]) => {
        const user = userDetails[userId];
        const isExpanded = expandedUsers.has(userId);

        return (
          <div
            key={userId}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-100 dark:border-gray-800"
          >
            <div
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800"
              onClick={() => toggleUser(userId)}
            >
              <div className="flex items-center space-x-4">
                {user?.photoURL ? (
                  <img
                    src={variantURL(user.photoURL, 200)}
                    onError={fallbackToOriginal(user.photoURL)}
                    alt={user.name}
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-800"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center ring-2 ring-gray-100 dark:ring-gray-800">
                    <span className="text-gray-500 dark:text-gray-400 text-2xl">
                      {user?.name?.charAt(0) || "?"}
                    </span>
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{user?.name || "Unknown User"}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Connected with {connections.length} {connections.length === 1 ? 'person' : 'people'}</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex -space-x-2">
                  {connections.slice(0, 3).map((connection, index) => {
                    const connectedUser = userDetails[connection.userId];
                    return (
                      <div key={index} className="relative">
                        {connectedUser?.photoURL ? (
                          <img
                            src={variantURL(connectedUser.photoURL, 200)}
                            onError={fallbackToOriginal(connectedUser.photoURL)}
                            alt={connectedUser.name}
                            className="w-8 h-8 rounded-full object-cover border-2 border-white dark:border-gray-800"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center border-2 border-white dark:border-gray-800">
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              {connectedUser?.name?.charAt(0) || "?"}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {connections.length > 3 && (
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center border-2 border-white dark:border-gray-800">
                      <span className="text-gray-500 dark:text-gray-400 text-xs">+{connections.length - 3}</span>
                    </div>
                  )}
                </div>
                {isExpanded ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </div>
            </div>

            {isExpanded && (
              <div className="border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                {connections.map((connection, index) => {
                  const connectedUser = userDetails[connection.userId];
                  const isChatOpen = openChats.has(connection.matchId);
                  return (
                    <div key={index}>
                      <div
                        className="flex items-center space-x-4 p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                        onClick={() => toggleChat(connection.matchId)}
                      >
                        {connectedUser?.photoURL ? (
                          <img
                            src={variantURL(connectedUser.photoURL, 200)}
                            onError={fallbackToOriginal(connectedUser.photoURL)}
                            alt={connectedUser.name}
                            className="w-12 h-12 rounded-full object-cover ring-1 ring-gray-200 dark:ring-gray-700"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center ring-1 ring-gray-200 dark:ring-gray-700">
                            <span className="text-gray-500 dark:text-gray-400 text-lg">
                              {connectedUser?.name?.charAt(0) || "?"}
                            </span>
                          </div>
                        )}
                        <div className="flex-grow">
                          <div className="text-base font-medium text-gray-900 dark:text-gray-100">{connectedUser?.name || "Unknown User"}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Connected on {new Date(connection.timestamp).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      {isChatOpen && (
                        <div className="px-4 pb-4">
                          <Chat
                            title={`Chat with ${connectedUser?.name || "Unknown User"}`}
                            parentID={connection.matchId}
                            parentCollection="matches"
                            subCollectionName={"Chat"}
                            showInput={false}
                            onSend={(text, uid, name, type, notifyTarget) =>
                              MatchApi.addMessageToChat(connection.matchId, "Chat", {
                                message: text,
                                userID: uid,
                                name,
                                ...(type && { type }),
                                ...(notifyTarget && { notifyTarget })
                              })
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
